<template>
  <div class="campaign-details">
    <!-- only show the page if there's some data from API response -->
    <template v-if="campaignData">
      <!-- Header and Actions -->
      <div class="d-flex flex-column flex-md-row justify-space-between mb-8 flex-wrap">
        <div class="text-h6">
          {{ campaignData.name }}
        </div>

        <!-- Actions -->
        <div class="d-flex flex-column flex-md-row align-start align-md-center flex-wrap mt-4 mt-md-0">
          <!-- Only show if there's some selected row -->
          <v-btn
            v-if="selectedRows.length"
            depressed
            color="primary"
            @click="markCompleted"
            class="mr-md-4"
            :disabled="isLoading"
            :class="{ 'shadow--primary': !isLoading }"
          >
            <v-icon left>
              done_all
            </v-icon>
            Mark As Completed
          </v-btn>

          <!-- Refresh status for selected rows -->
          <v-btn
            v-if="selectedRows.length"
            depressed
            color="primary"
            @click="triggerRefreshData"
            class="mr-md-4"
            :disabled="isLoading"
            :class="{ 'shadow--primary': !isLoading }"
          >
            <v-icon left>
              refresh
            </v-icon>
            Refresh
          </v-btn>

          <!-- Create New Campaign Link -->
          <v-btn
            depressed
            color="primary"
            @click="shouldShowFormDialog = true"
            :disabled="isLoading"
            :class="{ 'shadow--primary': !isLoading }"
          >
            <v-icon left>
              edit
            </v-icon>
            Edit
          </v-btn>
        </div>
      </div>

      <!-- Data Table -->
      <v-data-table
        v-model="selectedRows"
        :headers="tableHeaders"
        :items="tableItems"
        hide-default-footer
        disable-pagination
        :mobile-breakpoint="100"
        show-select
        class="elevation-1 custom-table"
      >
        <!-- hide the checkbox to select all from header -->
        <template v-slot:header.data-table-select></template>

        <!-- show user profile -->
        <template v-slot:item.user_id="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="30">
              <img :src="item.user_avatar ? proxyUrl(item.user_avatar, true) : null" />
            </v-avatar>

            <div class="d-flex flex-column ml-3">
              <div class="text-subtitle-2 line-height-1 mb-1 font-weight-bold">
                {{ item.user_id }}
              </div>
              <div class="text-subtitle-1 line-height-1">
                {{ item.user_name }}
              </div>
            </div>
          </div>
        </template>

        <!-- make the link clickable -->
        <template v-slot:item.post_link="{ item }">
          <a :href="item.post_url" target="_blank">
            {{ item.url_to_show }}
          </a>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            :class="item.is_complete ? 'green' : 'primary'"
            small
            dark
          >
            {{ capitalizeString(item.is_complete ? "Completed" : "Active") }}
          </v-chip>
        </template>
      </v-data-table>
    </template>

    <!-- show the popup form if needed -->
    <edit-campaign-form
      v-if="shouldShowFormDialog"
      :campaign-data="campaignData"
      @close="shouldShowFormDialog = false"
      @refresh="fetchItems"
    />
  </div>
</template>

<script>
const EditCampaignForm = () => import("@/components/campaigns/EditForm.vue")

export default {
  name: "CampaignDetails",

  components: {
    EditCampaignForm
  },

  data: () => ({
    isLoading: false,
    shouldShowFormDialog: false,

    selectedRows: [],

    // related to table view
    tableHeaders: [
      {
        text: "Sr. No.",
        value: "index",
        sortable: false,
      },
      {
        text: "User Profile",
        value: "user_id",
        sortable: false,
      },
      {
        text: "Followers",
        value: "user_followers",
        sortable: false,
      },
      {
        text: "Post Link",
        value: "post_link",
        sortable: false,
      },
      {
        text: "KPI",
        value: "post_kpi",
        sortable: false,
      },
      {
        text: "No. of Likes",
        value: "post_likes",
        sortable: false,
      },
      {
        text: "No. of Views",
        value: "post_views",
        sortable: false,
      },
      {
        text: "No. of Comments",
        value: "post_comments",
        sortable: false,
      },
      {
        text: "Status",
        value: "status",
        sortable: false
      }
    ],

    campaignData: null
  }),

  computed: {
    tableItems() {
      return this.campaignData
        ? this.campaignData.posts
          .map((item, index) => {
            const structuredURL = new window.URL(item.post_url)

            return {
              ...item,
              index: index + 1,
              user_id: `@${item.user_id}`,
              url_to_show: `instagram.com/${structuredURL.pathname}`,
              user_followers: nFormatter(item.user_followers),
              post_likes: nFormatter(item.post_likes),
              post_views: nFormatter(item.post_views),
              post_comments: nFormatter(item.post_comments),
              isSelectable: !item.is_complete
            }
          })
        : []
    }
  },

  methods: {
    /**
     * Make a call to the API to fetch the items
     */
    async fetchItems() {
      this.isLoading = true

      // hide the form if fetching data
      this.shouldShowFormDialog = false

      const loaderId = Symbol()
      this.$store.dispatch("loaders/add", loaderId)

      try {
        const response = await axios({
          url: "/api/campaigns/" + this.$route.params.campaignId
        })

        this.campaignData = response.data
      } catch (error) {
        logger({ type: "Campaign Details Fetch Error", error })
      } finally {
        this.$store.dispatch("loaders/remove", loaderId)

        this.isLoading = false
      }
    },

    /**
     * Updates status of selected rows
     */
    async markCompleted() {
      this.isLoading = true

      // For each selected row, make a POST request
      for (const row of this.selectedRows) {
        const loaderId = Symbol()
        this.$store.dispatch("loaders/add", loaderId)

        try {
          await axios({
            url: `/api/campaigns/${this.$route.params.campaignId}/posts/${row.id}`,
            method: "PUT",
            data: {
              is_complete: true
            }
          })
        } catch (error) {
          logger({ type: "Mark Campaign User Completed Error", error })
        } finally {
          this.$store.dispatch("loaders/remove", loaderId)
        }
      }

      // deselect rows
      this.isLoading = false
      this.selectedRows = []

      // now reload the data
      this.fetchItems()
    },

    /**
     * Updates the status and count values for selected rows
     */
    async triggerRefreshData() {
      this.isLoading = true

      // For each selected row, make a POST request
      for (const row of this.selectedRows) {
        const loaderId = Symbol()
        this.$store.dispatch("loaders/add", loaderId)

        try {
          await axios({
            url: `/api/campaigns/${this.$route.params.campaignId}/posts/${row.id}/refresh`,
            method: "POST"
          })
        } catch (error) {
          logger({ type: "Sync User Row Counts Error", error })
        } finally {
          this.$store.dispatch("loaders/remove", loaderId)
        }
      }

      // deselect rows
      this.isLoading = false
      this.selectedRows = []

      // show an alert message
      this.$store.dispatch("toasts/add", {
        text: "Please wait a few moments, we're refreshing counts..."
      })
    }
  },

  mounted() {
    // fetchItems on component load
    this.fetchItems()

    // start listening to the laravel echo connection
    window.echo
      .private(`campaigns.${this.$route.params.campaignId}`)
      .listen('CampaignStatsUpdated', (e) => {

        // check if this item exists in our list
        const index = this.campaignData.posts.findIndex((search) => search.id === e.campaignPost.id)

        // if found, replace it's values with incoming data
        if (index !== -1) {
          this.campaignData.posts.splice(index, 1, e.campaignPost)
        }
      })
  },

  beforeDestroy() {
    // disconnect from the pusher channel
    window.echo.leave(`campaigns.${this.$route.params.campaignId}`)
  }
}
</script>

<style lang="stylus">
.campaign-details
  .custom-table
    thead
      tr
        th
          overflow hidden
          white-space nowrap
</style>
